import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users as UsersIcon } from "lucide-react";

/**
 * Admin-only "View as employee" filter. Renders nothing for non-admins.
 *
 * Props:
 *   value     — current owner_id ("" = All employees)
 *   onChange  — (newOwnerId) => void
 *   testId    — optional data-testid override
 *
 * Usage:
 *   <EmployeeFilter value={ownerId} onChange={setOwnerId} />
 */
export default function EmployeeFilter({ value, onChange, testId = "employee-filter" }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => {
      setUsers((r.data || []).filter((u) => u.is_active));
    }).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin) return null;

  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger className="rounded-2xl h-11 w-full sm:w-56" data-testid={testId}>
        <UsersIcon className="h-4 w-4 mr-1.5 text-primary shrink-0" />
        <SelectValue placeholder="All employees" />
      </SelectTrigger>
      <SelectContent className="rounded-2xl">
        <SelectItem value="__all">All employees</SelectItem>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
